import { Component, lazy, Suspense, useState, type ReactNode } from 'react';
import { loadPage, PageLoadError } from '../lib/page-load';
import { PageState, StandalonePageState } from './PageState';

class Boundary extends Component<
  { children: ReactNode; retry: () => void; standalone?: boolean },
  { failed: boolean; reload: boolean }
> {
  state = { failed: false, reload: false };
  static getDerivedStateFromError(error: unknown) {
    return { failed: true, reload: error instanceof PageLoadError };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    const State = this.props.standalone ? StandalonePageState : PageState;
    return (
      <State
        description={
          this.state.reload
            ? '连接可能中断，或网站刚刚更新。请检查网络后重新载入，也可以先返回首页。'
            : '页面遇到了一点问题。可以重新打开；本机备份的文字可在重新进入练习后检查。'
        }
        retryLabel={this.state.reload ? '重新载入这一页' : '重新尝试'}
        onRetry={this.state.reload ? () => window.location.reload() : this.props.retry}
      />
    );
  }
}

export function createPageRoute<Props extends object>(
  load: () => Promise<{ default: (props: Props) => ReactNode }>,
  { standalone = false }: { standalone?: boolean } = {},
) {
  // Keep the component type stable while React retries a pending navigation.
  const InitialPage = lazy(() => loadPage(load));
  const State = standalone ? StandalonePageState : PageState;
  return function PageRoute(props: Props) {
    const [version, retry] = useState(0);
    return (
      <Boundary key={version} retry={() => retry((value) => value + 1)} standalone={standalone}>
        <Suspense fallback={<State kind="loading" />}>
          <InitialPage {...props} />
        </Suspense>
      </Boundary>
    );
  };
}
